// ═══════════════════════════════════════
// AUTH — Authentification & session
// Découplé de navigation.js : les callbacks SPA sont injectés
// depuis editor/main.js via setAuthChangeCallback().
// Sur les pages statiques, ces callbacks ne sont pas fournis.
// ═══════════════════════════════════════

import {
    sessionToken, currentUser, pendingAction,
    setSessionToken, setCurrentUser, setPendingAction,
    setCurrentPartitionMeta
} from '../state.js';
import { openModal, closeModal, navigate } from '../modal.js';
import { t } from '../i18n.js';

// Callback injecté par editor/main.js après login/logout
let _onAuthChange = () => {};
export function setAuthChangeCallback(fn) { _onAuthChange = fn; }

// Message d'erreur à afficher dès le montage de la vue login
let _pendingError = null;

export function authHeaders() {
    return sessionToken
        ? { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionToken }
        : { 'Content-Type': 'application/json' };
}

export function setSession(token, user) {
    setSessionToken(token);
    setCurrentUser(user);
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user));
    updateUserIcon();
}

export function clearSession() {
    setSessionToken(null);
    setCurrentUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    updateUserIcon();
}

export function updateUserIcon() {
    const indicator = document.getElementById('user-indicator');
    const btn       = document.getElementById('btn-user');
    const label     = document.getElementById('user-label');
    if (!indicator || !btn) return;
    if (currentUser) {
        indicator.textContent = '';
        btn.title = currentUser.username;
        if (label) label.textContent = currentUser.display_name || currentUser.username;
    } else {
        indicator.textContent = '';
        btn.title = t('auth.btn_account');
        if (label) label.textContent = t('auth.btn_login');
    }
    document.body.classList.toggle('is-admin', currentUser?.role === 'admin');
}

// ─────────────────────────────────────────
// Ouverture de la modale utilisateur
// ─────────────────────────────────────────
export function openUserModal(view = null, error = null) {
    const target = view ?? (currentUser ? 'user-account' : 'user-login');
    _pendingError = error;
    return openModal(target, _mountUserView);
}

// ─────────────────────────────────────────
// _mountUserView
// ─────────────────────────────────────────
async function _mountUserView(nav, controller) {
    const { signal } = controller;
    _wirePasswordToggles(signal);

    // ── Login ─────────────────────────────────────────────────────────────
    if (document.getElementById('login-email')) {
        document.querySelector('.btn-confirm')
            ?.addEventListener('click', () => submitLogin(), { signal });
        document.querySelector('.btn-cancel')
            ?.addEventListener('click', () => closeModal(), { signal });
        document.getElementById('login-password')
            ?.addEventListener('keydown', (e) => { if (e.key === 'Enter') submitLogin(); }, { signal });
        setTimeout(() => document.getElementById('login-email')?.focus(), 50);

        if (_pendingError) {
            const errEl = document.getElementById('login-error');
            if (errEl) {
                errEl.textContent = _pendingError;
                errEl.style.color = 'var(--muted, #888)';
            }
            _pendingError = null;
        }
    }

    // ── Forgot ────────────────────────────────────────────────────────────
    else if (document.getElementById('forgot-email')) {
        document.querySelector('.btn-confirm')
            ?.addEventListener('click', () => submitForgot(), { signal });
        document.getElementById('forgot-email')
            ?.addEventListener('keydown', (e) => { if (e.key === 'Enter') submitForgot(); }, { signal });
        setTimeout(() => document.getElementById('forgot-email')?.focus(), 50);
    }

    // ── Account ───────────────────────────────────────────────────────────
    else if (document.getElementById('account-name')) {
        document.getElementById('account-name').textContent  = currentUser?.username ?? '';
        document.getElementById('account-email').textContent = currentUser?.email    ?? '';
        document.querySelector('.btn-cancel')
            ?.addEventListener('click', () => closeModal(), { signal });
        document.querySelector('.btn-confirm')
            ?.addEventListener('click', () => submitLogout(), { signal });

        const subLink = document.getElementById('account-menu-subscription');
        if (subLink && currentUser?.id === 3) {
            subLink.style.display = '';
        }
    }

    // ── Edit account ──────────────────────────────────────────────────────
    else if (document.getElementById('edit-username')) {
        document.getElementById('edit-username').value  = currentUser?.username ?? '';
        document.getElementById('edit-email').value     = currentUser?.email    ?? '';
        document.getElementById('edit-password').value  = '';
        document.getElementById('edit-password2').value = '';
        document.querySelector('.btn-confirm')
            ?.addEventListener('click', () => submitEditAccount(), { signal });
    }

    // ── Groups — liste ────────────────────────────────────────────────────
    else if (document.getElementById('groups-list')) {
        const { loadGroupsList, openGroupCreate } = await import('../groups.js');
        loadGroupsList();
        document.getElementById('uv-groups-create')
            ?.addEventListener('click', () => openGroupCreate(), { signal });
    }

    // ── Group — édition/création ──────────────────────────────────────────
    else if (document.getElementById('uv-group-edit-save')) {
        const { submitGroupSave, submitGroupDelete } = await import('../groups.js');
        document.getElementById('uv-group-edit-save')
            ?.addEventListener('click', () => submitGroupSave(), { signal });
        document.getElementById('uv-group-delete')
            ?.addEventListener('click', (e) => { e.preventDefault(); submitGroupDelete(); }, { signal });
    }

    // ── Group — assignation partition ─────────────────────────────────────
    else if (document.getElementById('group-assign-list')) {
        const { submitGroupAssign } = await import('../groups.js');
        document.querySelector('.btn-cancel')
            ?.addEventListener('click', () => closeModal(), { signal });
        document.querySelector('.btn-confirm')
            ?.addEventListener('click', () => submitGroupAssign(), { signal });
    }

    // ── Reset password ────────────────────────────────────────────────────
    else if (document.getElementById('reset-submit')) {
        document.getElementById('reset-submit')
            ?.addEventListener('click', () => submitReset(), { signal });
        document.getElementById('reset-password2')
            ?.addEventListener('keydown', (e) => { if (e.key === 'Enter') submitReset(); }, { signal });
    }

    // ── Delete account — assistant en étapes ──────────────────────────────
    // Toutes les vues de l'assistant portent #delete-wizard ; l'étape est
    // dans data-step. Le détail vit dans common/account-delete.js.
    else if (document.getElementById('delete-wizard')) {
        const { startAccountDeletion, mountDeleteStep } = await import('./account-delete.js');
        if (document.getElementById('delete-wizard').dataset.step === 'intro') {
            startAccountDeletion(signal);
        } else {
            mountDeleteStep(signal);
        }
    }

    // ── Upgrade ───────────────────────────────────────────────────────────
    else if (document.getElementById('upgrade-message')) {
        document.querySelector('.btn-cancel')
            ?.addEventListener('click', () => closeModal(), { signal });
    }
}

// ─────────────────────────────────────────
// Afficher / masquer un mot de passe
// Les libellés viennent du fragment (data-label-show / data-label-hide),
// déjà rendus dans la bonne langue par api/modals.php.
// ─────────────────────────────────────────
function _wirePasswordToggles(signal) {
    document.querySelectorAll('.modal-user .password-toggle').forEach(btn => {
        btn.addEventListener('click', () => {
            const input = document.getElementById(btn.dataset.target);
            if (!input) return;
            const visible = input.type === 'password';
            input.type = visible ? 'text' : 'password';
            btn.classList.toggle('is-visible', visible);
            btn.setAttribute('aria-pressed', String(visible));
            const label = visible ? btn.dataset.labelHide : btn.dataset.labelShow;
            btn.setAttribute('aria-label', label);
            btn.title = label;
            input.focus();
        }, { signal });
    });
}

// ─────────────────────────────────────────
// Soumissions
// ─────────────────────────────────────────

export async function submitLogin() {
    const email    = document.getElementById('login-email').value.trim();
    const password = document.getElementById('login-password').value;
    const errEl    = document.getElementById('login-error');
    try {
        const res  = await fetch('/api/users.php?action=login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        });
        const data = await res.json();
        if (res.ok) {
            setSession(data.token, {
                id:       data.id,
                username: data.username,
                email:    data.email,
                role:     data.role,
            });
            closeModal();

            // Ces URL n'existent que pour ouvrir la modale correspondante
            // (voir main.js) : aucune page réelle derrière, un reload dessus
            // donne un 404 une fois connecté. On rentre à l'accueil à la place.
            const isAuthOnlyPage = ['login', 'account', 'reset-password']
                .some(p => location.pathname.includes(p));
            if (isAuthOnlyPage) {
                location.href = '/';
            } else {
                location.reload();
            }
        } else {
            errEl.style.color = '';
            errEl.textContent = data.error || 'Erreur';
        }
    } catch(e) { errEl.textContent = t('auth.error.network'); }
}

export async function submitForgot() {
    const email = document.getElementById('forgot-email').value.trim();
    const errEl = document.getElementById('forgot-error');
    try {
        const res  = await fetch('/api/users.php?action=forgot-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
        const data = await res.json();
        errEl.style.color = '#27ae60';
        errEl.textContent = data.message || t('auth.forgot_sent');
    } catch(e) { errEl.textContent = t('auth.error.network'); }
}

export async function submitLogout() {
    if (sessionToken) {
        await fetch('/api/users.php?action=logout', {
            method: 'POST',
            headers: authHeaders()
        }).catch(() => {});
    }
    clearSession();
    location.reload();
}

export async function submitEditAccount() {
    const username  = document.getElementById('edit-username').value.trim();
    const email     = document.getElementById('edit-email').value.trim();
    const password  = document.getElementById('edit-password').value;
    const password2 = document.getElementById('edit-password2').value;
    const errEl     = document.getElementById('edit-error');

    errEl.textContent = '';

    if (password && password !== password2) {
        errEl.textContent = t('auth.error.passwords_mismatch');
        return;
    }

    const body = { username, email };
    if (password) body.password = password;

    try {
        const res  = await fetch('/api/users.php?action=update', {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(body)
        });
        const data = await res.json();
        if (res.ok) {
            setCurrentUser({ ...currentUser, username, email });
            localStorage.setItem('auth_user', JSON.stringify({ ...currentUser, username, email }));
            updateUserIcon();
            navigate('user-account');
        } else {
            errEl.textContent = data.error || t('auth.error.update_failed');
        }
    } catch(e) { errEl.textContent = t('auth.error.network'); }
}

export async function submitReset() {
    const token     = document.getElementById('reset-token').value;
    const password  = document.getElementById('reset-password').value;
    const password2 = document.getElementById('reset-password2').value;
    const errEl     = document.getElementById('reset-error');

    errEl.textContent = '';

    if (password !== password2) {
        errEl.textContent = t('auth.error.passwords_mismatch');
        return;
    }
    if (password.length < 6) {
        errEl.textContent = t('auth.error.password_short');
        return;
    }

    console.log('[reset-password] token envoyé:', token, '(longueur:', token.length, ')');

    try {
        const res  = await fetch('/api/users.php?action=reset-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, password })
        });
        const data = await res.json();
        console.log('[reset-password] réponse serveur:', res.status, data);
        if (res.ok) {
            errEl.style.color = '#27ae60';
            errEl.textContent = t('auth.reset_success');
            document.getElementById('reset-password').value  = '';
            document.getElementById('reset-password2').value = '';
            history.replaceState({}, '', location.pathname);
            setTimeout(() => navigate('user-login'), 2000);
        } else {
            errEl.style.color = '';
            errEl.textContent = data.error || t('auth.error.invalid_link');
        }
    } catch(e) {
        errEl.textContent = t('auth.error.network');
    }
}